import { CardPlaceholder, Product } from '@bucketplace/common';
import {
  FONT_STYLE,
  MEDIA_QUERY,
  ResizeImage,
  useResponsive,
} from '@bucketplace/design-system';
import { Grid, GridItem } from '@bucketplace/design-system/bds';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { stringify } from 'querystring';
import { Fragment, ReactElement } from 'react';
import useSWRInfinite from 'swr/infinite';
import { ProductCardSectionItem } from '@/components/Product/ProductCardSectionItem';
import { getExhibitionSectionItems } from '@/promotions/api/promotion';
import { useUserContext } from '@/provider';
import {
  checkAppVersion,
  getAppOsInfo,
  isMobile as isMobileDevice,
} from '@/utils';

import { InfiniteScroll } from './InfiniteScroll';

const LIST_SIZE = 20;

interface ExhibitionSectionItemsProps {
  id: string;
  detailId: string;
  sectionId: string;
}

export function ExhibitionSectionItems({
  id,
  detailId,
  sectionId,
}: ExhibitionSectionItemsProps): ReactElement | null {
  const { userInfo } = useUserContext();
  const isLoggedIn = !!userInfo?.user;
  const { osType, osVersion } = getAppOsInfo() ?? {};

  let showCouponBadge = true;

  if (
    !checkAppVersion({
      osType,
      currentVersion: osVersion,
      androidVersion: '23.5.0',
      iosVersion: '23.5.0',
    })
  ) {
    showCouponBadge = false;
  }

  const { data, isValidating, size, setSize } = useSWRInfinite(
    (pageIndex: number, prevPageData) => {
      if (prevPageData && !prevPageData.length) {
        return null;
      }

      return `/api/promotions/${id}/section-items?${stringify({
        detailId,
        sectionId,
        page: pageIndex,
        isLoggedIn,
        showCouponBadge,
      })}`;
    },
    getExhibitionSectionItems,
  );

  const { breakpoint } = useResponsive({});
  const isMobile = breakpoint === 'mobile';

  const sectionItems = (data ? data.flat() : []).filter(
    (item) => item.item?.isSelling && !item.item.isDiscontinued,
  );
  const hasNext = (data?.flat()?.length ?? 0) >= LIST_SIZE * size;

  if (data == null) {
    return (
      <>
        {Array.from({ length: LIST_SIZE }).map((_, index) => (
          <StyledGridItem
            key={`${index + 1}`}
            column={{
              mobile: 'half',
              tablet: 'half',
              laptop: 4,
            }}
          >
            <CardPlaceholder />
          </StyledGridItem>
        ))}
      </>
    );
  }

  return (
    <InfiniteScroll
      onLoad={() => {
        setSize(size + 1);
      }}
      hasMore={hasNext}
      pending={isValidating}
      tagName="ul"
    >
      <SectionItems
        gap={{
          mobile: '0 16',
          tablet: '30 16',
          laptop: '30 20',
        }}
      >
        {sectionItems?.map((sectionItem, index) => {
          const imageUrl =
            isMobile || isMobileDevice()
              ? sectionItem.mobileImageUrl
              : sectionItem.pcImageUrl;
          const isFirstItem = index === 0;
          const isFirstSectionItem =
            index > 0 &&
            sectionItem.sectionId !== sectionItems[index - 1]?.sectionId;

          return (
            <Fragment key={`${sectionItem.item?.id}_${index + 1}`}>
              {(isFirstItem || isFirstSectionItem) &&
                (sectionItem.title || imageUrl) && (
                  <StyledGridItem
                    column={{
                      mobile: 'full',
                      tablet: 'full',
                      laptop: 'full',
                    }}
                  >
                    <a
                      {...(sectionItem?.linkUrl
                        ? { href: sectionItem.linkUrl }
                        : {})}
                    >
                      {imageUrl ? (
                        <BannerImage src={imageUrl} alt="섹션 배너" />
                      ) : (
                        <Title>{sectionItem.title}</Title>
                      )}
                    </a>
                  </StyledGridItem>
                )}
              <StyledGridItem
                column={{
                  mobile: 'half',
                  tablet: 'half',
                  laptop: 4,
                }}
              >
                <ProductCardSectionItem
                  css={{
                    padding: '15px 0',
                  }}
                  product={sectionItem.item as Product}
                  logData={{
                    section: '기획전 디테일',
                    index,
                  }}
                />
              </StyledGridItem>
            </Fragment>
          );
        })}
      </SectionItems>
    </InfiniteScroll>
  );
}

const SectionItems = styled(Grid)`
  ${MEDIA_QUERY.max(
    'tablet',
    `
    padding: 0 16px;
  `,
  )}
`;

const StyledGridItem = styled(GridItem)`
  &:first-of-type {
    > p {
      margin: 0 -10px -10px;
      ${MEDIA_QUERY.max(
        'tablet',
        css`
        margin: 0px -16px 4px;
      `,
      )}
    }
  }
  &:not(:first-of-type) {
    > p {
      margin: 40px -10px -10px;
      ${MEDIA_QUERY.max(
        'tablet',
        css`
        margin: 24px -16px 4px;
      `,
      )}
    }
  }
`;

const Title = styled.p`
  ${FONT_STYLE.title1}
  padding: 34px 0;
  background-color: ${({ theme }) => theme.content.base5};
  text-align: center;
  font-weight: 700;
  ${({ theme }) =>
    MEDIA_QUERY.max(
      'tablet',
      css`
    ${FONT_STYLE.title2}
    padding: 20px 0;
    background-color: ${theme.content.base4};
  `,
    )}
`;

const BannerImage = styled(ResizeImage)`
  width: 100%;
  display: block;
  margin: 0 0 20px;
`;
